import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy, signal, computed } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { filter, map, ReplaySubject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { createLiveStreamUrl, formatLiveStreamUrl } from 'app/modules/social/services/livestream/livestream.helper';
import { LivestreamService } from 'app/modules/social/services/livestream/livestream.service';
import { LivestreamForm } from 'app/modules/social/services/livestream/livestream.types';
import { selectors } from 'app/modules/social/state/app';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { isUndefined } from 'app/utils/stream-util';
import { Presentation, Presenter, PresenterType } from 'messages/websocket.messages';

import { headerInfo, LivestreamType, livestreamError, CurrentView } from './add-livestream-modal.types';

export interface ModalDialogData {
  title: string;
  text: string;
  primaryButtonText: string;
  secondaryButtonText?: string;
}

interface LivestreamMedia {
  audio?: MediaStream;
  audioId: string;
  video?: MediaStream;
  videoId: string;
}
export type LivestreamMediaStreams = Record<string, LivestreamMedia>;

@Component({
  selector: 'modal-add-livestream',
  templateUrl: './add-livestream-modal.component.html',
  styleUrls: ['./add-livestream-modal.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalAddLivestreamComponent extends Loggable implements OnInit, OnDestroy {
  private dialogRef = inject<MatDialogRef<ModalAddLivestreamComponent>>(MatDialogRef);
  private data = inject<{ currentView: CurrentView; livestream: Presenter }>(MAT_DIALOG_DATA);
  private fb = inject(UntypedFormBuilder);
  private livestreamService = inject(LivestreamService);
  private store = inject(Store);

  public readonly validationMessage = signal<string | undefined>(undefined);
  public readonly label = signal(new UntypedFormControl());
  public readonly url = signal(new UntypedFormControl());
  public readonly livestreamType = signal<LivestreamType | undefined>(undefined);
  public readonly livestreamTypeSelected = signal<LivestreamType>(LivestreamType.RTMP);
  public readonly isLivestreamActive = signal(false);
  public readonly isPresentationFull = signal(false);
  public readonly showPreviewSection = signal(false);
  public readonly isPreviewLoading = signal(false);
  public readonly previewError = signal(false);
  public readonly broadcastNow = signal(true);
  public readonly currentView = signal<CurrentView | undefined>(undefined);
  public readonly hlsForm = signal<UntypedFormGroup | null>(null);
  public readonly rtmpForm = signal<UntypedFormGroup | null>(null);

  private readonly livestream = signal<Presenter | undefined>(undefined);
  private readonly streamId = signal<string | undefined>(undefined);
  private destroyed$ = new ReplaySubject<boolean>(1);
  private readonly socialId = signal<string | undefined>(undefined);
  private readonly presentation = signal<Presentation | undefined>(undefined);
  private readonly isCrossRoom = signal(false);

  private readonly isFormFillingRequired = signal(true);
  private readonly toReplaceLivestream = signal<Presenter | undefined>(undefined);

  public constructor() {
    super();
    const data = this.data;

    this.setDisplayName('AddLivestreamModalComponent');
    this.currentView.set(data.currentView);
    this.livestream.set(data.livestream);

    // If the currentView is SelectPresenters
    // The livestream has not yet been added to the queue
    if (this.currentView() === CurrentView.SELECT_PRESENTERS) {
      this.broadcastNow.set(false);
    }

    const hlsFormGroup = this.fb.group({
      labelStream: new UntypedFormControl('', [Validators.required]),
      streamUrl: new UntypedFormControl('', [Validators.required]),
    });
    this.hlsForm.set(hlsFormGroup);

    const rtmpFormGroup = this.fb.group({
      labelStream: new UntypedFormControl('', [Validators.required]),
      streamUrl: new UntypedFormControl('', [Validators.required]),
      streamKey: new UntypedFormControl('', [Validators.required]),
    });
    this.rtmpForm.set(rtmpFormGroup);
  }

  public ngOnInit(): void {
    this.store
      .select(selectors.isPresentationFull)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationFull) => this.isPresentationFull.set(isPresentationFull));

    this.store
      .select(selectors.isLivestreamActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isLivestreamActive) => this.isLivestreamActive.set(isLivestreamActive));

    this.store
      .select(selectors.getPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation) => this.presentation.set(presentation));

    this.store
      .select(selectors.isCrossRoomPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCrossRoom) => this.isCrossRoom.set(isCrossRoom));

    this.store
      .select(selectors.getCurrentSocial)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((social) => {
        const { socialId } = social ?? {};
        const { peerConnectionURL } = social?.socialMeta ?? {};
        this.socialId.set(socialId);
        this.rtmpForm()?.get('streamKey')?.patchValue(uuidv4());
        this.rtmpForm()?.get('streamUrl')?.patchValue(formatLiveStreamUrl(peerConnectionURL));
      });

    if (this.livestream()) {
      this.checkForLivestream(this.livestream()!).catch((e) => {
        this.error('Failed to check/start livestream preview:', e);
      });
    }
  }

  // If a livestream has been passed to the modal
  // Only show the preview of the stream and not the setup
  private async checkForLivestream(data: Presenter): Promise<void> {
    if (data) {
      const { description: streamURL, firstName: labelStream, userId: oldStreamId } = data;

      this.isPreviewLoading.set(true);
      this.showPreviewSection.set(true);
      this.broadcastNow.set(false);
      this.isFormFillingRequired.set(false);
      this.livestream.set(undefined);
      this.toReplaceLivestream.set(data);

      // Remove old livestream;
      await this.livestreamService.removeLivestreamFromQueue(oldStreamId);

      // Start a new livestream using the same URL, creating a copy of the old livestream;
      try {
        const newStreamId = await this.livestreamService.restartLivestreamPreview(oldStreamId, streamURL);
        if (newStreamId) {
          this.streamId.set(newStreamId);
          this.subscribeLivestreamPreview(newStreamId);
        }
      } catch (error) {
        this.previewError.set(true);
        this.back();
        this.error(`Error starting the livestream ${labelStream} - ${streamURL}: ${error}`);
      }
      return;
    }
    this.showPreviewSection.set(false);
  }

  private handleMediaStreamChange(stream: MediaStream | undefined, id: string): void {
    const videoElement = document.getElementById('livestream-video') as HTMLVideoElement;
    if (!stream || !videoElement) {
      return;
    }

    if (videoElement.getAttribute('data-stream-id') === id) {
      this.trace('handleMediaStreamChange: skipping video stream update, stream already set as source');
      return;
    }
    videoElement.srcObject = stream ?? null;
    videoElement.setAttribute('data-stream-id', id);
    if (!videoElement.oncanplay) {
      videoElement.oncanplay = async () => {
        videoElement.oncanplay = null;
        try {
          await videoElement.play();
          this.previewError.set(false);
          this.isPreviewLoading.set(false);
        } catch (e) {
          this.previewError.set(true);
          this.isPreviewLoading.set(false);
          this.error('handleMediaStreamChange: Failed to play video', e);
        }
      };
    }
  }

  public onInputKeyDown(e: Event): void {
    e.stopPropagation();
  }

  private hasInfoToReplaceLivestream(): boolean {
    return !!this.toReplaceLivestream() && !this.isFormFillingRequired();
  }

  private getLiveStreamFormObject(): LivestreamForm {
    const form: LivestreamForm =
      this.livestreamType() === LivestreamType.HLS ? this.hlsForm()!.value : this.rtmpForm()!.value;
    return form;
  }

  private async startLiveStream(labelStream: string, streamUrl: string, streamKey?: string) {
    assertDefined(this.socialId());
    try {
      const streamId = await this.livestreamService.startLiveStream(createLiveStreamUrl(streamUrl, streamKey));
      this.streamId.set(streamId);
      this.subscribeLivestreamPreview(streamId);
    } catch (error) {
      this.previewError.set(true);
      this.back();
      this.error(`Error starting the livestream ${labelStream} - ${streamUrl}: ${error}`);
    }
  }

  // End preview and add stream to queue
  // Can receive the label and the stream url as parameters for the cases where the form isn't filled
  private addStreamToQueue(directLabel?: string, directUrl?: string): void {
    const { labelStream, streamKey, streamUrl } = this.getLiveStreamFormObject();
    const streamIdValue = this.streamId();
    const socialIdValue = this.socialId();

    if (streamIdValue && socialIdValue) {
      const newLivestream = {
        labelStream: directLabel ? directLabel : labelStream,
        createUrl: directUrl ? false : true,
        streamUrl: directUrl ? directUrl : streamUrl,
        streamKey,
        streamId: streamIdValue,
        socialId: socialIdValue,
      };
      this.livestreamService.addLivestreamToQueue(newLivestream);

      if (!this.livestream()) {
        const presenterObj: Presenter = { ...newLivestream, isGuest: false, userId: streamIdValue };
        this.livestream.set(presenterObj);
      }
    }
  }

  // Request that stream starts publishing and adds it as an active presenter
  // Can receive the label and the stream url as parameters for the cases where the form isn't filled
  private publishLivestream(directLabel?: string, directUrl?: string): void {
    const { labelStream, streamKey, streamUrl } = this.getLiveStreamFormObject();
    const streamIdValue = this.streamId();
    const socialIdValue = this.socialId();

    if (streamIdValue) {
      const newLivestream: Presenter = {
        userId: streamIdValue,
        isGuest: false,
        description: directUrl ? directUrl : createLiveStreamUrl(streamUrl, streamKey),
        firstName: directLabel ? directLabel : labelStream,
        presenterType: PresenterType.LIVE_STREAM,
        socialId: socialIdValue,
      };

      this.livestreamService.addActiveLivestream(newLivestream, this.isCrossRoom());

      if (!this.livestream()) {
        this.livestream.set(newLivestream);
      }
    }
  }

  private subscribeLivestreamPreview(livestreamId: string): void {
    this.livestreamService.livestreamMediaStream$
      .pipe(
        map((streams: LivestreamMediaStreams) => {
          const { video: stream, videoId: id } = streams[livestreamId] ?? {};
          return { id, stream };
        }),
        filter(({ id }) => !!id),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ id, stream }) => this.handleMediaStreamChange(stream, id));
  }

  public isButtonDisabled(buttonType: 'next' | 'ok'): boolean {
    if (this.livestreamType() === LivestreamType.HLS && buttonType === 'next') {
      return !this.hlsForm()?.valid;
    }
    if (this.livestreamType() === LivestreamType.RTMP && buttonType === 'next') {
      return !this.rtmpForm()?.valid;
    }

    if (this.isPreviewLoading() && buttonType === 'ok') {
      return true;
    }
    return false;
  }

  public async onPrimaryClicked(buttonType: string): Promise<void> {
    if (buttonType === 'next') {
      if (isUndefined(this.livestreamType())) {
        this.livestreamType.set(this.livestreamTypeSelected());
      } else {
        this.isPreviewLoading.set(true);
        this.showPreviewSection.set(true);
        this.broadcastNow.set(true);
        if (this.currentView() === CurrentView.SELECT_PRESENTERS) {
          this.broadcastNow.set(false);
        }
        // Handle preview of video
        const { labelStream, streamKey, streamUrl } = this.getLiveStreamFormObject();
        if (labelStream && streamUrl) {
          await this.startLiveStream(labelStream, streamUrl, streamKey).catch((e) => this.error(e));
        }
      }
    }

    if (buttonType === 'ok' && this.livestream() && CurrentView.MANAGE_PRESENTERS) {
      this.dialogRef.close();
      if (this.broadcastNow()) {
        // Checks if there's information to restart the livestream
        if (this.hasInfoToReplaceLivestream()) {
          this.publishLivestream(this.toReplaceLivestream()?.firstName, this.toReplaceLivestream()?.description);
        } else {
          this.publishLivestream();
        }
      }
    }

    if (buttonType === 'ok' && !this.livestream() && this.currentView() === CurrentView.MANAGE_PRESENTERS) {
      this.dialogRef.close();

      if (!this.broadcastNow()) {
        if (this.hasInfoToReplaceLivestream()) {
          this.addStreamToQueue(this.toReplaceLivestream()?.firstName, this.toReplaceLivestream()?.description);
        } else {
          this.addStreamToQueue();
        }
      }

      if (this.broadcastNow() && this.presentation()) {
        if (this.hasInfoToReplaceLivestream()) {
          this.publishLivestream(this.toReplaceLivestream()?.firstName, this.toReplaceLivestream()?.description);
        } else {
          this.publishLivestream();
        }
      }
    }

    if (buttonType === 'ok' && !this.livestream() && this.currentView() === CurrentView.SELECT_PRESENTERS) {
      this.dialogRef.close();
      if (this.hasInfoToReplaceLivestream()) {
        this.addStreamToQueue(this.toReplaceLivestream()?.firstName, this.toReplaceLivestream()?.description);
      } else {
        this.addStreamToQueue();
      }
    }

    // Resets variables for new modals to be open
    if (buttonType === 'ok') {
      this.toReplaceLivestream.set(undefined);
      this.isFormFillingRequired.set(true);
    }
  }

  public back(): boolean {
    if (this.showPreviewSection() && this.livestreamType()) {
      this.showPreviewSection.set(false);

      if (this.streamId()) {
        this.livestreamService.endLiveStream(this.streamId()!);
      }
      return true;
    }

    if (!this.showPreviewSection() && this.livestreamType()) {
      this.livestreamType.set(undefined);
      this.previewError.set(false);
      return true;
    }

    if (this.showPreviewSection() && this.livestreamType() && this.previewError()) {
      return true;
    }
    return false;
  }

  /**
   * Function called when user clicks on 'Cancel' button
   *
   * Don't remove livestream when previewing a queued livestream, instead, just unsubscribe.
   */
  public onSecondaryClicked(): void {
    if (this.streamId() && !this.data) {
      this.livestreamService.removeLivestreamSubscriber(this.streamId()!);
      this.streamId.set(undefined);
    }
    if (this.data && this.streamId()) {
      //Move livestream back to queue
      this.addStreamToQueue(this.data.livestream.firstName, this.data.livestream.description);
    }
    this.dialogRef.close();
  }

  public readonly headerInfo = computed(() => {
    if (
      isUndefined(this.livestreamType()) ||
      (isUndefined(headerInfo[this.livestreamType()!]) && !this.showPreviewSection())
    ) {
      return headerInfo.default;
    }

    if (this.livestreamType() && this.showPreviewSection()) {
      return headerInfo.preview;
    }

    if (isUndefined(this.livestreamType()) && !this.showPreviewSection()) {
      return headerInfo.default;
    }

    return headerInfo[this.livestreamType()!];
  });

  public getQueueText(): string {
    let text = 'Queue for later broadcast';
    if (this.currentView() === CurrentView.SELECT_PRESENTERS) {
      text = 'Queue for broadcast';
    }
    return text;
  }

  public getPreviewError() {
    return livestreamError.error;
  }

  public get livestreamServicesTypes(): string[] {
    return Object.values(LivestreamType);
  }

  public ngOnDestroy(): void {
    if (this.streamId() && !this.broadcastNow()) {
      this.livestreamService.unSubscribeLivestream(this.streamId()!);
    }

    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
